import {
  isCyrillicMiddleName,
  isCyrillicName,
  isFixedFloat,
  isFloat,
  isInteger,
  isIntOrFloat,
  isIntOrFloatWithDot,
  isOwnershipShareField,
  isSimpleChars,
  isTaxPeriodField,
  isVin,
  isYearsString,
  onlyCharsAndNumbers,
  onlyCharsNumbersSymb,
  isRuNumberSpecials,
  isLatinNumberSpecials,
  onlyLettersNumbersSymb,
} from '../inputTypes';
import { curryRight, flow, isFunction, reduce, trimStart } from 'lodash';

/**
 * Util to combine multiple filters as normalizers
 * @example normalize={ combineFilters([numberOnly, limitedLength(6)]) }
 */

export function combineFilters(filters) {
  return (val, prevVal) => {
    return filters.reduce((result, filter) => {
      return filter(result, prevVal);
    }, val);
  };
}

/**
 * Only float number is allowed to typing
 * !Note: Use as normalize
 * @example <Field ... normalize={floatNumberOnly} />
 * @param val
 * @param prevVal
 * @returns {*}
 */
export function floatNumberOnly(val, prevVal) {
  if (val === '') {
    return val;
  }
  if (val.slice(-1) === '.' && isInteger(val.slice(0, -1))) {
    return val;
  }
  if (!isInteger(val) && !isFloat(val)) {
    return prevVal;
  }
  if (val.indexOf('.') !== -1 && val.length > prevVal.length) {
    return parseFloat(val).toFixed(2).toString();
  }
  return val;
}

/**
 * @param maxFloatLength
 * @param allowFirstEmpty - allow using of empty integer number part
 * @returns Function
 */
export function fixedFloatNumberOnly(maxFloatLength, allowFirstEmpty) {
  /**
   * Only fixed float number is allowed to typing
   * !Note: Use as normalize
   * @example <Field ... normalize={floatNumberOnly} />
   * @param val
   * @param prevVal
   * @returns {*}
   */
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }
    if (!isFixedFloat(val, maxFloatLength, allowFirstEmpty)) {
      return prevVal;
    }
    return val;
  };
}

export function fixedFloatNumberOnlyWithMaxlength(maxInt, maxLength) {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }
    if (!isIntOrFloat(val, maxInt, maxLength)) {
      return prevVal;
    }
    return val;
  };
}

export function fixedFloatNumberWithMaxlengthOnlyDot(maxInt, maxLength) {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }
    if (!isIntOrFloatWithDot(val, maxInt, maxLength)) {
      return prevVal;
    }
    return val;
  };
}

/**
 * Only number is allowed to typing
 * !Note: Use as normalize
 * @example <Field ... normalize={numberOnly} />
 * @param val
 * @param prevVal
 * @returns {*}
 */
export function numberOnly(val, prevVal) {
  if (val === '') {
    return val;
  }
  if (!isInteger(val)) {
    return prevVal;
  }
  return val;
}

/**
 * Allowed any input types except alphabetical characters
 * @example <Field ... normalize={disableChars} />
 * @param val
 * @param prevVal
 * @returns {*}
 */
export function disableChars(val, prevVal) {
  if (val === '') {
    return val;
  }

  if (isSimpleChars(val)) {
    return prevVal;
  }
  return val;
}

/**
 * Only alphabetical chars is allowed
 * !Note: Use as normalize
 * @example <Field ... normalize={charsOnly} />
 * @param val
 * @param prevVal
 * @returns {*}
 */
export function charsOnly(val, prevVal) {
  if (val === '') {
    return val;
  }

  if (!isSimpleChars(val)) {
    return prevVal;
  }

  return val;
}

/**
 * Numbers range between from and to
 * !Note: Use as normalize
 * @example <Field ... normalize={numbersRange(1,10)} />
 * @param from
 * @param to
 * @returns {function(*=, *)}
 */
export function numbersRange(from, to) {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }
    if (!isInteger(val)) {
      return prevVal;
    }
    if (val < from) {
      return prevVal;
    }
    if (val > to) {
      return prevVal;
    }
    return val;
  };
}

/**
 * Splice string up to length
 * @param upTo
 * @returns {function(*)}
 */
export function limitedLength(upTo) {
  return (val) => {
    if (val && val.length >= upTo) {
      return val.slice(0, upTo);
    }
    return val;
  };
}

export function yearsStringOnly(val, prevVal) {
  if (!isYearsString(val)) {
    return prevVal;
  }

  return val;
}

export function alphaNumeric() {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }
    if (!onlyCharsAndNumbers(val)) {
      return prevVal;
    }
    return val;
  };
}

export function alphaNumericAndSymb() {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }
    if (!onlyCharsNumbersSymb(val)) {
      return prevVal;
    }
    return val;
  };
}

export function lettersNumericAndSymb() {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }
    if (!onlyLettersNumbersSymb(val)) {
      return prevVal;
    }
    return val;
  };
}

export function excludeStartWithSpaces(val = '', prevVal) {
  if (val.startsWith(' ')) {
    return prevVal;
  }
  return val;
}

export function excludeStartWithNull(val = '', prevVal) {
  if (val.startsWith('0')) {
    return prevVal;
  }
  return val;
}

export function cyrillicName(val, prevVal) {
  if (val === '') {
    return val;
  }

  if (!isCyrillicName(val)) {
    return prevVal;
  }

  return val;
}

export function ruNumberSpecials(val, prevVal) {
  if (val === '') {
    return val;
  }

  if (!isRuNumberSpecials(val)) {
    return prevVal;
  }

  return val;
}

export function latinNumberSpecials(val, prevVal) {
  if (val === '') {
    return val;
  }

  if (!isLatinNumberSpecials(val)) {
    return prevVal;
  }

  return val;
}

export function cyrillicMiddleName(val, prevVal) {
  if (val === '') {
    return val;
  }

  if (!isCyrillicMiddleName(val)) {
    return prevVal;
  }

  return val;
}

export function hasMaxValue(maxSum) {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }

    if (+val > +maxSum) {
      return prevVal;
    }

    return val;
  };
}

export function remainingValue(remaining) {
  return (val, prevVal) => {
    if (val === '') {
      return val;
    }

    if (+prevVal + +remaining < val) {
      return prevVal;
    }

    return val;
  };
}

export function ownershipShareField(val, prevVal) {
  if (val === '') {
    return val;
  }

  if (!isOwnershipShareField(val)) {
    return prevVal;
  }

  return val;
}

export function taxPeriodValue(val, prevVal) {
  if (val.trim() === '') {
    return val.trim();
  }
  if (!isTaxPeriodField(val)) {
    return prevVal;
  }
  return val;
}

export function isVinField(val = '', prevVal) {
  if (val === '') {
    return val;
  }

  val = val.toUpperCase();

  if (prevVal && val) {
    const str = val.substr(0, prevVal.length);

    if (str === prevVal) {
      const valuePart = val.substr(prevVal.length - 1, val.length);
      if (!isVin(valuePart)) {
        return prevVal;
      }
    }
  }

  if (!prevVal && val) {
    if (!isVin(val)) {
      return prevVal;
    }

    return val;
  }

  if (!isVin(val)) {
    return isVin(prevVal) ? prevVal : '';
  }

  return val;
}

export const multipleSpacesNormalize = (val, prevVal) => {
  if (val.length < 2) {
    return trimStart(val);
  }
  if (val[val.length - 1] === val[val.length - 2] && val[val.length - 1] === ' ') {
    return prevVal;
  }
  return val;
};

export const combineNormalize = (normalizeArrayFn) => {
  return function (value, prevValue) {
    const curriedFn = reduce(
      normalizeArrayFn,
      (accum, normalizeFn) => {
        if (isFunction(normalizeFn)) {
          accum.push(curryRight(normalizeFn)(prevValue));
        }
        return accum;
      },
      [],
    );
    return flow(...curriedFn)(value);
  };
};

export const normalizeNumber3_15 = fixedFloatNumberWithMaxlengthOnlyDot(3, 15);
